import { Typography, Input, Row, Col, Space } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import { useState } from 'react'
import { HelpMessage } from 'utils/constants'
import { googleSheetFormItemName } from '../../../utils'
import { googleSheetURLFormat } from 'shared'
import HelpIcon from 'components/HelpIcon'
import CIMSLink from 'components/CIMSLink'
import CIMSSelect from 'components/CIMSSelect'

const CreateGoogleSheetBody: React.FC = () => {
  const [type, setType] = useState('url')
  const help = HelpMessage.PayItems.Manage.Connections.GoogleSheet
  return (
    <>
      <Typography.Paragraph>
        Prior to creating a Google Sheet connection in CIMS, the Document
        associated with the desired worksheet, or tab, must be{' '}
        <CIMSLink
          external
          href="https://support.google.com/drive/answer/2494822?hl=en"
        >
          shared
        </CIMSLink>{' '}
        with{' '}
        <Typography.Text copyable type="warning">
          cims-google-sheet-bot@horrocks-cims-email-bot.iam.gserviceaccount.com
        </Typography.Text>
        , or publicly available. Failure to do so will result in errors during
        syncs. Documents can be re-shared after creation if access is
        accidentally revoked.
      </Typography.Paragraph>
      <Typography.Paragraph>
        Each desired sheet, or tab, must have its own connection.
      </Typography.Paragraph>
      <Typography.Paragraph>
        CIMS will attempt to sync several cell properties for each cell in the
        linked Google Sheet.
      </Typography.Paragraph>
      <Typography.Paragraph>
        <Space size="small">
          <HelpIcon text="The contents of a cell as text. If CIMS can find a URL in the formatted value with an empty space on either side, it will be usable as a hyperlink in CIMS">
            <Typography.Text code>formattedValue</Typography.Text>
          </HelpIcon>
          <HelpIcon text="If the HYPERLINK function was used to attach a URL to a cell, it will be usable as a hyperlink in CIMS">
            <Typography.Text code>hyperlink</Typography.Text>
          </HelpIcon>
          <HelpIcon text="Notes attached to a cell will act as a tooltip in CIMS. These are not the same as Comments">
            <Typography.Text code>note</Typography.Text>
          </HelpIcon>
        </Space>
      </Typography.Paragraph>
      <CIMSFormItem
        label="Pay Item Column Key"
        help={help.PayItemColumnKey}
        name={googleSheetFormItemName('payItemColumnKey')}
        initialValue="Pay Items"
        description={
          <Typography.Paragraph type="secondary">
            Defaults to <Typography.Text code>Pay Items</Typography.Text>
          </Typography.Paragraph>
        }
      >
        <Input allowClear />
      </CIMSFormItem>
      <Row gutter={32}>
        <Col span={12}>
          <CIMSFormItem help={help.ImportWith} label={'Import With'}>
            <CIMSSelect
              simple
              value={type}
              onChange={(value) => {
                setType(value.toString())
              }}
              options={[
                {
                  value: 'url',
                  label: 'URL',
                },
                {
                  value: 'ids',
                  label: 'IDs',
                },
              ]}
            />
          </CIMSFormItem>
        </Col>
      </Row>
      <Row gutter={32}>
        {type === 'url' ? (
          <Col span={24}>
            <CIMSFormItem
              label="URL"
              url
              help={`A URL to the Google Sheet. Expected to take the form ${googleSheetURLFormat}`}
              name={googleSheetFormItemName('url')}
              requiredMessage={'Please enter a URL'}
            >
              <Input allowClear />
            </CIMSFormItem>
          </Col>
        ) : (
          <>
            <Col span={12}>
              <CIMSFormItem
                label="Document ID"
                help={help.DocumentId}
                name={googleSheetFormItemName('documentId')}
                requiredMessage={'Please enter a document ID'}
              >
                <Input allowClear />
              </CIMSFormItem>
            </Col>
            <Col span={12}>
              <CIMSFormItem
                label="Sheet ID"
                help={help.SheetId}
                name={googleSheetFormItemName('sheetId')}
                requiredMessage={'Please enter a sheet ID'}
              >
                <Input allowClear />
              </CIMSFormItem>
            </Col>
          </>
        )}
      </Row>
    </>
  )
}

export default CreateGoogleSheetBody
